// REST API helpers and the reconnecting game socket. The session is an HttpOnly cookie, which this code can't
// read: the browser sends it, and a 401 is how the client finds out it has none.

import { C2S, PROTOCOL_VERSION, RoomSummary, S2C, SIGNED_OUT_CLOSE, VoiceReply, VoiceRequest, VoiceSettings, VoiceSettingsChange } from '../shared/protocol';
import { AnalyticsConfig } from '../shared/analytics';
import { ClassId } from '../shared/types';
import { analytics } from './analytics';

export interface HeroSummary {
  id: string;
  name: string;
  cls: ClassId;
  level: number;
  progress: number;
  updated: number;
}

/**
 * `?as=name` gives a tab its own identity, for testing multiplayer on one machine. The server only honors it in
 * test mode, where it chooses a separate cookie.
 */
export function testIdentity(): string | null {
  const as = new URLSearchParams(location.search).get('as');
  return as && /^[a-z0-9]{1,16}$/.test(as) ? as : null;
}

/** A code on its way to the account's own address. */
export interface OwnCode {
  ok: true;
  email: string;
  testCode?: string;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

/**
 * How long a voice call can take before it's given up on. Longer than a room can reasonably spend on
 * one request, which can make two calls to Realtime at up to 10 seconds each.
 */
export const VOICE_REQUEST_MS = 30_000;

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const as = testIdentity();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(as ? { 'x-warp-as': as } : {}), ...(init.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  if (!res.ok) throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status, body.code);
  return body as T;
}

export interface SiteConfig {
  turnstileSiteKey: string | null;
  testMode: boolean;
  analytics?: AnalyticsConfig;
}

export interface AccountInfo {
  email: string | null;
  sessions: { id: string; label: string; created_at: number; last_used_at: number }[];
  current: string;
}

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const api = {
  config: () => call<SiteConfig>('/api/config'),
  askForCode: (email: string, turnstile: string | null) => call<{ ok: true; resendIn: number; testCode?: string }>('/api/auth/code', post({ email, turnstile })),
  verifyCode: (email: string, code: string) => call<{ email: string; isNew?: boolean }>('/api/auth/verify', post({ email, code })),
  signOut: () => call<{ ok: true }>('/api/auth/signout', post({})),
  account: () => call<AccountInfo>('/api/account'),
  signOutOthers: () => call<{ ok: true }>('/api/account/signout-others', post({})),
  startAddressChange: () => call<OwnCode>('/api/account/email/start', post({})),
  sendToNewAddress: (code: string, newEmail: string, turnstile: string | null) => call<{ ok: true; testCode?: string }>('/api/account/email/new', post({ code, newEmail, turnstile })),
  confirmNewAddress: (code: string, newEmail: string, signOutOthers: boolean) => call<{ email: string }>('/api/account/email/confirm', post({ code, newEmail, signOutOthers })),
  startDelete: () => call<OwnCode>('/api/account/delete/start', post({})),
  confirmDelete: (code: string) => call<{ ok: true }>('/api/account/delete/confirm', post({ code })),
  heroes: () => call<{ heroes: HeroSummary[]; email: string | null; accountId?: string }>('/api/heroes'),
  createHero: (name: string, cls: ClassId) => call<{ hero: HeroSummary }>('/api/heroes', post({ name, cls })).then((r) => r.hero),
  deleteHero: (id: string) => call<{ ok: boolean }>(`/api/heroes/${id}`, { method: 'DELETE' }),
  rooms: () => call<{ rooms: RoomSummary[] }>('/api/rooms').then((r) => r.rooms),
  createRoom: (name: string, diff: number, isPublic: boolean) => call<{ code: string; name: string; diff: number }>('/api/rooms', post({ name, diff, isPublic })),
  roomInfo: (code: string) => call<{ code: string; name: string; diff: number; players: number; max: number; version: string | null; colo: string }>(`/api/rooms/${code}`),
  voice: (code: string, req: VoiceRequest) => call<VoiceReply>(`/api/rooms/${code}/voice`, { method: 'POST', body: JSON.stringify(req), signal: AbortSignal.timeout(VOICE_REQUEST_MS) }),
  voiceSettings: () => call<VoiceSettings>('/api/voice/settings', { signal: AbortSignal.timeout(VOICE_REQUEST_MS) }),
  // keepalive, because muting someone and leaving straight away shouldn't lose the mute.
  changeVoiceSettings: (change: VoiceSettingsChange) => call<{ ok: true }>('/api/voice/settings', { method: 'POST', body: JSON.stringify(change), keepalive: true, signal: AbortSignal.timeout(VOICE_REQUEST_MS) }),
};

export type SocketEvents = {
  onMessage: (m: S2C) => void;
  onStatus: (status: 'connecting' | 'open' | 'closed', detail?: string) => void;
  /** `code` says which failure this is, and unlike the message it never holds a room code. */
  onFatal: (message: string, code: string) => void;
  /** The room closed for an update. Progress is already saved. */
  onRoomClosed: () => void;
  /** This session is over, so reconnecting can't work. */
  onSignedOut: () => void;
};

const MAX_RETRIES = 6;
const MAX_INUSE_RETRIES = 10;
/** How long to keep trying when a hero's save can't be reached, which can outlast the hero's lease. */
const MAX_UNAVAILABLE_MS = 120_000;
const UNAVAILABLE_RETRY_MS = 3_000;

export class GameSocket {
  private ws: WebSocket | null = null;
  private retries = 0;
  private inuseRetries = 0;
  /** When the room first said this hero's save can't be reached, or 0 if it hasn't. */
  private unavailableSince = 0;
  private closedByUs = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private code: string, private heroId: string, private ev: SocketEvents) {}

  connect(): void {
    if (this.closedByUs) return;
    this.ev.onStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const as = testIdentity();
    const ws = new WebSocket(`${proto}://${location.host}/ws/room/${this.code}${as ? `?as=${as}` : ''}`);
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      ws.send(JSON.stringify({ t: 'join', hero: this.heroId, v: PROTOCOL_VERSION, sid: analytics.sessionId() } satisfies C2S));
      this.ev.onStatus('open');
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let msg: S2C;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.t === 'closed') {
        this.closedByUs = true;
        this.ev.onRoomClosed();
        return;
      }
      if (msg.t === 'welcome') {
        this.inuseRetries = 0;
        this.unavailableSince = 0;
      }
      if (msg.t === 'err') {
        if (msg.code === 'auth') {
          this.signedOut();
          return;
        }
        if (msg.code === 'unavailable') {
          this.unavailableSince ||= Date.now();
          if (Date.now() - this.unavailableSince < MAX_UNAVAILABLE_MS) return;
        }
        // Leaving one room and entering another races the hero's lease release, and a room that
        // just closed for an update is back in a moment; the close handler below retries shortly.
        // Anything else, or waiting too long, is final.
        if ((msg.code === 'inuse' || msg.code === 'updating') && this.inuseRetries < MAX_INUSE_RETRIES) {
          this.inuseRetries++;
          return;
        }
        this.closedByUs = true;
        this.ev.onFatal(msg.m, msg.code);
        return;
      }
      this.ev.onMessage(msg);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws || this.closedByUs) return;
      if (e.code === SIGNED_OUT_CLOSE) {
        this.signedOut();
        return;
      }
      if (e.code === 4003) {
        this.closedByUs = true;
        this.ev.onFatal('This hero was opened in another window.', 'opened_elsewhere');
        return;
      }
      this.ev.onStatus('closed');
      if (this.retries >= MAX_RETRIES) {
        this.closedByUs = true;
        this.ev.onFatal('Lost connection to the room.', 'connection_lost');
        return;
      }
      const wait = this.unavailableSince ? UNAVAILABLE_RETRY_MS : 400 * 2 ** this.retries;
      this.retries++;
      // A refused upgrade looks just like a dropped connection from here, so ask whether the
      // room still exists rather than retrying against something that's gone for good.
      api.roomInfo(this.code).catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) this.signedOut();
        if (err instanceof ApiError && err.status === 404 && !this.closedByUs) {
          this.closedByUs = true;
          if (this.retryTimer) clearTimeout(this.retryTimer);
          this.ev.onFatal('That room does not exist.', 'no_such_room');
        }
      });
      this.retryTimer = setTimeout(() => this.connect(), wait);
    };
  }

  private signedOut(): void {
    if (this.closedByUs) return;
    this.closedByUs = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.ev.onSignedOut();
  }

  send(msg: C2S): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.closedByUs = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    try {
      this.ws?.close(1000, 'leaving');
    } catch {
      /* already closed */
    }
    this.ws = null;
  }
}
